import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { RCL as useTranslation } from "../../components/RCL";
import utils from "../../utils/gatsby-helpers";
import { useAlerts } from "../../context";
import { BaseLayout } from "../../components/Layouts/BaseLayout";
import { FluidSections } from "../../components/FluidSections";
import { HeaderTilesConnector } from "../../components/HeaderTiles";
import { DescriptionTabs } from "../../components/Country/DescriptionTabs/DescriptionTabs";
import { HotelTilesConnector } from "../../components/Destination/HotelTiles/HotelTilesConnector";
import { Tabs, Tab } from "../../components/TabsAndAccordions";
import { ThingsToDoCardsConnector } from "../../components/ThingsToDo/ThingsToDoCards/ThingsToDoCardsConnector";
import { Section } from "../../sharedComponents/Section/Section";

const Destination = ({ data, pageContext, location }) => {
	const pageData = data.contentfulDestinations;
	const altLangPages = data.allSitePage;

	const { allHotels, sunwingFavourites, topRatedAdult, topRatedFamily, topRatedLuxury } = data;

	const { alertsCount } = useAlerts();

	// Parse the Banner information

	let bannerInfo = null;
	let markers = null;
	if (allHotels?.nodes?.length > 0) {
		markers = allHotels.nodes.map(hotel => {
			const hotelData = hotel.data;
			const destinationName = hotelData.DestinationName;
			const countryName = hotelData.RegionName;

			return {
				name: hotelData.HotelName,
				longitude: hotelData.ResortOverview?.Longitude,
				latitude: hotelData.ResortOverview?.Latitude,
				link: {
					url:
						hotelData.HotelName &&
						utils.getHotelURL(
							pageContext?.locale?.slice(0, 2),
							countryName,
							destinationName,
							hotelData.HotelName
						),
					linkText: hotelData.HotelName,
				},
			};
		});
	}
	if (pageData?.imageCarousel || pageData?.bannerCards) {
		bannerInfo = {
			primaryTile: {
				...pageData?.imageCarousel,
				logo: pageData?.logo,
				logoPosition: pageData?.logoPosition,
				detailMap: markers && {
					zoom: 6,
					markers,
				},
			},
			otherTiles: pageData?.bannerCards,
		};
	}

	const allDisplayHotels = allHotels?.nodes?.filter(Boolean) || [];
	const allSunwingFavourites = sunwingFavourites?.nodes?.filter(Boolean) || [];
	const allTopRatedAdult = topRatedAdult?.nodes?.filter(Boolean) || [];
	const allTopRatedFamily = topRatedFamily?.nodes?.filter(Boolean) || [];
	const allTopRatedLuxury = topRatedLuxury?.nodes?.filter(Boolean) || [];

	const hasHotelAndResorts =
		allDisplayHotels.length ||
		allSunwingFavourites.length ||
		allTopRatedAdult.length ||
		allTopRatedFamily.length ||
		allTopRatedLuxury.length;

	const hasExcursionsAndAttractions =
		(data?.excursions?.nodes ||
			data?.excursions?.nodes.length > 0 ||
			data?.attractions?.nodes ||
			data?.attractions?.nodes.length > 0) &&
		data?.excursions?.nodes?.filter(i => i?.nexusExcursionPageUrl)?.length > 0;

	const translations = {
		topHotelsAndResorts: useTranslation({
			searchKey: "top-hotels-and-resorts",
		}),
		thingsToDo: useTranslation({ searchKey: "top-things-to-do" }),
		viewAll: useTranslation({ searchKey: "view-all" }),
		topRatedLuxury: useTranslation({ searchKey: "top-rated-luxury" }),
		topRatedFamily: useTranslation({ searchKey: "top-rated-family" }),
		topRatedAdult: useTranslation({ searchKey: "top-rated-adult" }),
		sunwingFavourites: useTranslation({ searchKey: "sunwing-favourites" }),
		allResorts: useTranslation({ searchKey: "all-resorts" }),
	};

	return (
		<BaseLayout
			location={location}
			pageContext={pageContext}
			seo={pageData.seo}
			altLangPages={altLangPages}
			alerts={alertsCount}>
			<>
				<Section
					title={pageData.destinationName && pageData.displayTitle}
					titleCenter={false}
					titleAs="h1">
					{bannerInfo && <HeaderTilesConnector data={bannerInfo} locale={pageContext.locale} />}
				</Section>

				{(pageData?.overviewDescription ||
					pageData?.weddingsDescription ||
					pageData?.groupsDescription ||
					pageData?.travelTipsDescription) && (
					<Section padding="none">
						<DescriptionTabs
							overviewTab={pageData?.overviewDescription}
							weddingsTab={pageData?.weddingsDescription}
							groupsTab={pageData?.groupsDescription}
							travelTipsDescription={pageData?.travelTipsDescription}
						/>
					</Section>
				)}
				{!!hasHotelAndResorts && (
					<Section title={translations.topHotelsAndResorts} titleId="hotels">
						<Tabs variant="stone">
							{allDisplayHotels.length > 0 && (
								<Tab eventKey="allHotels" title={translations.allResorts}>
									<HotelTilesConnector hotels={allDisplayHotels} />
								</Tab>
							)}

							{allSunwingFavourites.length > 0 && (
								<Tab eventKey="sunwingFavourites" title={translations.sunwingFavourites}>
									<HotelTilesConnector hotels={allSunwingFavourites} />
								</Tab>
							)}

							{allTopRatedAdult.length > 0 && (
								<Tab eventKey="adult" title={translations.topRatedAdult}>
									<HotelTilesConnector hotels={allTopRatedAdult} />
								</Tab>
							)}

							{allTopRatedFamily.length > 0 && (
								<Tab eventKey="family" title={translations.topRatedFamily}>
									<HotelTilesConnector hotels={allTopRatedFamily} />
								</Tab>
							)}

							{allTopRatedLuxury.length > 0 && (
								<Tab eventKey="luxury" title={translations.topRatedLuxury}>
									<HotelTilesConnector hotels={allTopRatedLuxury} />
								</Tab>
							)}
						</Tabs>
					</Section>
				)}
				{!!hasExcursionsAndAttractions && (
					<Section
						title={translations.thingsToDo?.replace("{{DESTINATION}}", pageData?.destinationName)}
						titleAs="h2"
						titleSize="h1"
						titleId="things-to-do">
						<ThingsToDoCardsConnector
							location={location}
							isDetailsModal
							destinationCode={data?.contentfulDestinations?.destinationCode}
						/>
					</Section>
				)}
			</>
			<FluidSections data={pageData.pageSections} location={location} locale={pageContext.locale} />
		</BaseLayout>
	);
};

Destination.propTypes = {
	data: PropTypes.object.isRequired,
	pageContext: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
};
export default Destination;

export const pageQuery = graphql`
	query GetDestinationPageData(
		$id: String!
		$ctf_id: String!
		$locale: String!
		$localeRegex: String!
		$destination: String!
		$country: String!
		$destinationRegex: String!
	) {
		contentfulDestinations(id: { eq: $id }) {
			destinationName
			destinationCode
			pageSections {
				...pageSections
			}
			seo {
				...seoComponent
			}
			displayTitle
			imageCarousel {
				sys {
					contentType {
						sys {
							id
						}
					}
				}
				...mediaGallery
			}
			logo {
				url
				secure_url
				width
				height
				raw_transformation
				version
				public_id
				format
			}
			logoPosition
			bannerCards {
				...LinkTileComponent
			}
			overviewDescription {
				...ParagraphSection
			}
			weddingsDescription {
				...ParagraphSection
			}
			groupsDescription {
				...ParagraphSection
			}
			travelTipsDescription {
				...ParagraphSection
			}
		}
		allSitePage(filter: { context: { ctf_id: { eq: $ctf_id } } }) {
			edges {
				...languageToggleButton
			}
		}
		allHotels: allHotel(
			sort: { data: { StarRating: DESC } }
			filter: {
				node_locale: { eq: $locale }
				data: { DestinationName: { eq: $destination }, RegionName: { eq: $country } }
			}
		) {
			nodes {
				...hotelDetailsShort
				data {
					ResortOverview {
						Longitude
						Latitude
					}
				}
			}
		}
		sunwingFavourites: allHotel(
			sort: { data: { StarRating: DESC } }
			filter: {
				node_locale: { eq: $locale }
				data: {
					DestinationName: { eq: $destination }
					RegionName: { eq: $country }
					CollectionsSWG: {
						elemMatch: { name: { regex: "/^(Sunwing Favourites|Coups de cœur Sunwing)$/" } }
					}
				}
			}
		) {
			nodes {
				...hotelDetailsShort
			}
		}
		topRatedAdult: allHotel(
			sort: { data: { StarRating: DESC } }
			filter: {
				node_locale: { eq: $locale }
				data: {
					DestinationName: { eq: $destination }
					RegionName: { eq: $country }
					CollectionsSWG: {
						elemMatch: {
							name: { regex: "/^(Top rated adults resort|Les meilleurs hôtels pour adultes)$/" }
						}
					}
				}
			}
		) {
			nodes {
				...hotelDetailsShort
			}
		}
		topRatedFamily: allHotel(
			sort: { data: { StarRating: DESC } }
			filter: {
				node_locale: { eq: $locale }
				data: {
					DestinationName: { eq: $destination }
					RegionName: { eq: $country }
					CollectionsSWG: {
						elemMatch: {
							name: { regex: "/^(Top rated family resort|Le meilleur hotel pour families)$/" }
						}
					}
				}
			}
		) {
			nodes {
				...hotelDetailsShort
			}
		}
		topRatedLuxury: allHotel(
			sort: { data: { StarRating: DESC } }
			filter: {
				node_locale: { eq: $locale }
				data: {
					DestinationName: { eq: $destination }
					RegionName: { eq: $country }
					CollectionsSWG: {
						elemMatch: {
							name: { regex: "/^(Top rated luxury resort|Le meilleur hôtels de luxe)$/" }
						}
					}
				}
			}
		) {
			nodes {
				...hotelDetailsShort
			}
		}
		excursions: allContentfulMainExcursion(
			filter: {
				destinationCode: { regex: $destinationRegex }
				node_locale: { regex: $localeRegex }
				options: { elemMatch: { ttsFullCode: { regex: "//" } } }
				nexusExcursionPageUrl: { elemMatch: { destinationCode: { regex: $destinationRegex } } }
			}
		) {
			nodes {
				...mainExcursion
			}
		}
		thingsToDo: allContentfulThingsToDoContent(
			filter: {
				node_locale: { eq: $locale }
				destinationContent: { destinationCode: { regex: $destinationRegex } }
			}
		) {
			nodes {
				slug
			}
		}
	}
`;
